import { FC } from "react";
import {
  Box,
  Group,
  Text,
  Anchor,
  Stack,
  UnstyledButton,
  Image,
  Paper,
} from "@mantine/core";
import {
  IconBrandInstagram,
  IconBrandTiktok,
  IconBrandYoutube,
  IconWorld,
  IconBook,
} from "@tabler/icons-react";
import { getPresentationParamsForOrigin } from "../../../../getPresentationParamsForOrigin";

interface FooterLinkProps {
  href: string;
  icon: React.ReactNode;
  label: string;
}

const FooterLink: FC<FooterLinkProps> = ({ href, icon, label }) => (
  <UnstyledButton
    component="a"
    href={href}
    target="_blank"
    rel="noreferrer"
    style={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 6,
      padding: "8px 12px",
      borderRadius: 8,
      transition: "transform 0.2s",
    }}
    onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.05)")}
    onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
  >
    <Box
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: 44,
        height: 44,
        borderRadius: "50%",
        background: "var(--mantine-color-dark-6)",
      }}
    >
      {icon}
    </Box>
    <Text size="xs" c="dark.2">
      {label}
    </Text>
  </UnstyledButton>
);

export const AppFooter: FC = () => {
  const { logo, links, competitionName, ads } =
    getPresentationParamsForOrigin();
  const presentator = ads?.presentation;

  return (
    <Paper
      mt={50}
      mb={-16}
      pt="xl"
      pb={{
        base: 64,
        sm: "calc(var(--mantine-spacing-xl) + env(safe-area-inset-bottom, 0px))",
      }}
      radius={0}
      style={{
        // Bleed the background to the screen edge, keep content in the safe area
        // (--app-inset-* are set on AppShell.Main)
        marginInlineStart:
          "calc(-1 * (var(--app-inset-start, 0px) + var(--app-shell-padding)))",
        marginInlineEnd:
          "calc(-1 * (var(--app-inset-end, 0px) + var(--app-shell-padding)))",
        paddingInlineStart:
          "calc(var(--mantine-spacing-xl) + var(--app-inset-start, 0px))",
        paddingInlineEnd:
          "calc(var(--mantine-spacing-xl) + var(--app-inset-end, 0px))",
        background:
          "linear-gradient(135deg, var(--mantine-color-dark-8) 0%, var(--mantine-color-dark-9) 100%)",
      }}
    >
      <Stack gap="xl" align="center">
        {/* Logo */}
        <Image
          src={logo.light}
          alt={competitionName}
          h={40}
          w="auto"
          fit="contain"
        />

        {/* Social Media & Links */}
        {(links.website ??
          links.instagram ??
          links.tiktok ??
          links.youtube ??
          links.magazine) && (
          <Group gap="xs" justify="center">
            {links.website && (
              <FooterLink
                href={links.website}
                icon={<IconWorld size={22} color="white" />}
                label="Website"
              />
            )}
            {links.instagram && (
              <FooterLink
                href={links.instagram}
                icon={<IconBrandInstagram size={22} color="white" />}
                label="Instagram"
              />
            )}
            {links.tiktok && (
              <FooterLink
                href={links.tiktok}
                icon={<IconBrandTiktok size={22} color="white" />}
                label="TikTok"
              />
            )}
            {links.youtube && (
              <FooterLink
                href={links.youtube}
                icon={<IconBrandYoutube size={22} color="white" />}
                label="YouTube"
              />
            )}
            {links.magazine && (
              <FooterLink
                href={links.magazine}
                icon={<IconBook size={22} color="white" />}
                label="Magazin"
              />
            )}
          </Group>
        )}

        {/* Presentation Sponsor */}
        {presentator && (
          <Anchor
            href={presentator.linkUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Image
              alt={presentator.name}
              src={presentator.logoUrl}
              h={48}
              w="auto"
              fit="contain"
            />
          </Anchor>
        )}

        {/* Divider Line */}
        <Box
          w={60}
          h={2}
          style={{
            background: "var(--mantine-color-fwcBlue-5)",
            borderRadius: 1,
          }}
        />

        {/* Links */}
        <Group gap="lg" justify="center">
          <Anchor
            href={links.imprint}
            target="_blank"
            rel="noreferrer"
            size="sm"
            c="dark.2"
            underline="hover"
          >
            Impressum
          </Anchor>
          <Anchor
            href={links.privacy}
            target="_blank"
            rel="noreferrer"
            size="sm"
            c="dark.2"
            underline="hover"
          >
            Datenschutz
          </Anchor>
          {links.feedback && (
            <Anchor
              href={links.feedback}
              target="_blank"
              rel="noreferrer"
              size="sm"
              c="dark.2"
              underline="hover"
            >
              App-Feedback
            </Anchor>
          )}
        </Group>

        {/* Copyright & Credit */}
        <Stack gap={4} align="center">
          <Text size="xs" c="dark.3">
            © {new Date().getFullYear()} {competitionName}
          </Text>
          <Text size="xs" c="dark.4">
            Technik & App von{" "}
            <Anchor
              href={`https://www.patrickhilker.de/?utm_source=customer-link&utm_term=${competitionName}-app-footer`}
              target="_blank"
              rel="noreferrer"
              size="xs"
              c="dark.3"
              underline="hover"
            >
              Patrick Hilker
            </Anchor>
          </Text>
        </Stack>
      </Stack>
    </Paper>
  );
};
